export interface OpeningPeriod {
  open: string;
  close: string;
}

export type WeekDayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type ShopOpeningHoursForm = Partial<Record<WeekDayKey, OpeningPeriod[]>>;

const HHMM_RE = /^\d{2}:\d{2}$/;
export const OPENING_HOUR_DAYS: Array<{ key: WeekDayKey; label: string }> = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

export function normalizeOpeningPeriods(value: unknown): OpeningPeriod[] {
  if (value == null) return [];

  let raw: unknown[] = [];
  if (Array.isArray(value)) {
    raw = value;
  } else if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (record.isOpen === false) return [];
    if (Array.isArray(record.periods)) {
      raw = record.periods;
    } else if (record.open || record.close) {
      raw = [record];
    }
  }

  const periods: OpeningPeriod[] = [];
  for (const slot of raw) {
    if (!slot || typeof slot !== 'object') continue;
    const open = String((slot as { open?: unknown }).open || '');
    const close = String((slot as { close?: unknown }).close || '');
    if (!HHMM_RE.test(open) || !HHMM_RE.test(close) || open === close) continue;
    periods.push({ open, close });
  }

  return periods.sort((left, right) => left.open.localeCompare(right.open));
}

export function normalizeShopOpeningHoursForm(value: unknown): ShopOpeningHoursForm {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
  const normalized: ShopOpeningHoursForm = {};
  for (const { key } of OPENING_HOUR_DAYS) {
    const periods = normalizeOpeningPeriods(source[key]);
    if (periods.length > 0) {
      normalized[key] = periods;
    }
  }
  return normalized;
}

export function formatOpeningPeriods(value: unknown): string {
  const periods = normalizeOpeningPeriods(value);
  if (periods.length === 0) return 'Fermé';
  return periods.map((period) => `${period.open} - ${period.close}`).join(', ');
}
